import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import { triggerDelivery, triggerDeliveryDryRun } from '../services/delivery-runs.service';
import type { TriggerDeliveryRequest } from '../types/delivery-runs.types';

/** Variables for a trigger: the connection to deliver from + the request body. */
export interface TriggerDeliveryVars {
  connectionId: string;
  body: TriggerDeliveryRequest;
}

/**
 * Trigger error handler. `409` = delivery disabled for the connection (server
 * sends a structured `{error_code, message}` detail, surfaced here); `422` =
 * the request failed validation. Everything else is a generic failure.
 */
export function useTriggerError() {
  const { t } = useTranslation('deliveries');
  return (err: unknown) => {
    const status = err instanceof ApiError ? err.statusCode : null;
    if (status === 409) {
      toast.warning(extractApiError(err, t('trigger.toast.disabled')));
      return;
    }
    if (status === 422) {
      toast.warning(extractApiError(err, t('trigger.toast.invalid')));
      return;
    }
    toast.error(extractApiError(err, t('trigger.toast.error')));
  };
}

/**
 * Trigger a delivery (or dry-run). The caller passes its own `onSuccess` to
 * `mutate` to navigate to the created run's detail (`job.id` is the run id);
 * this hook toasts and refreshes the runs list.
 */
export function useTriggerDelivery(isDryRun = false) {
  const { t } = useTranslation('deliveries');
  const queryClient = useQueryClient();
  const onError = useTriggerError();
  return useMutation({
    mutationFn: ({ connectionId, body }: TriggerDeliveryVars) =>
      isDryRun ? triggerDeliveryDryRun(connectionId, body) : triggerDelivery(connectionId, body),
    onSuccess: () =>
      toast.success(isDryRun ? t('trigger.toast.dryRunQueued') : t('trigger.toast.queued')),
    onError,
    onSettled: () => void queryClient.invalidateQueries({ queryKey: queryKeys.deliveries.all }),
  });
}
